import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const AutoLoanCalculatorPage = () => {
  const [calcMode, setCalcMode] = useState('price'); // 'price' or 'payment'
  
  // Total Price Mode
  const [autoPrice, setAutoPrice] = useState('50000');
  const [loanTerm, setLoanTerm] = useState('60');
  const [interestRate, setInterestRate] = useState('5');
  const [cashIncentives, setCashIncentives] = useState('');
  const [downPayment, setDownPayment] = useState('10000');
  const [tradeInValue, setTradeInValue] = useState('');
  const [amountOwedOnTrade, setAmountOwedOnTrade] = useState('');
  const [salesTax, setSalesTax] = useState('7');
  const [titleFees, setTitleFees] = useState('500');
  const [includeTaxesInLoan, setIncludeTaxesInLoan] = useState(true);
  
  // Monthly Payment Mode (reverse calculation)
  const [monthlyPayment, setMonthlyPayment] = useState('500');
  
  const [result, setResult] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleView, setScheduleView] = useState('yearly');

  // States that don't reduce sales tax for trade-ins
  const noTradeInTaxReduction = ['California', 'District of Columbia', 'Hawaii', 'Kentucky', 'Maryland', 'Michigan', 'Montana', 'Virginia'];

  const usStates = [
    '-- Select --', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana',
    'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
    'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const [selectedState, setSelectedState] = useState('-- Select --');

  const calculateFromPrice = () => {
    const price = parseFloat(autoPrice) || 0;
    const term = parseInt(loanTerm) || 60;
    const rate = (parseFloat(interestRate) || 0) / 100;
    const incentives = parseFloat(cashIncentives) || 0;
    const down = parseFloat(downPayment) || 0;
    const tradeIn = parseFloat(tradeInValue) || 0;
    const owedOnTrade = parseFloat(amountOwedOnTrade) || 0;
    const taxRate = (parseFloat(salesTax) || 0) / 100;
    const fees = parseFloat(titleFees) || 0;

    // Calculate taxable amount based on state
    const tradeInCredit = Math.max(0, tradeIn - owedOnTrade);
    let taxableAmount = price - incentives;
    if (!noTradeInTaxReduction.includes(selectedState)) {
      taxableAmount = taxableAmount - tradeInCredit;
    }
    const salesTaxAmount = Math.max(0, taxableAmount * taxRate);

    // Calculate loan amount
    let loanAmount = price - incentives - down - tradeInCredit;
    if (includeTaxesInLoan) {
      loanAmount += salesTaxAmount + fees;
    }
    loanAmount = Math.max(0, loanAmount);

    // Calculate upfront payment
    let upfrontPayment = down;
    if (!includeTaxesInLoan) {
      upfrontPayment += salesTaxAmount + fees;
    }

    // Calculate monthly payment
    const monthlyRate = rate / 12;
    let payment;
    if (monthlyRate === 0) {
      payment = loanAmount / term;
    } else {
      payment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, term)) / 
                (Math.pow(1 + monthlyRate, term) - 1);
    }

    const totalLoanPayments = payment * term;
    const totalInterest = totalLoanPayments - loanAmount;
    const totalCost = price + salesTaxAmount + fees + totalInterest - incentives;

    // Generate amortization schedule
    const schedule = generateSchedule(loanAmount, monthlyRate, payment, term);

    setResult({
      monthlyPayment: payment,
      loanAmount,
      salesTaxAmount,
      upfrontPayment,
      totalLoanPayments,
      totalInterest,
      totalCost,
      term,
      fees,
      principalPercent: ((loanAmount / totalLoanPayments) * 100).toFixed(0),
      interestPercent: ((totalInterest / totalLoanPayments) * 100).toFixed(0),
      schedule,
      yearlySchedule: generateYearlySchedule(schedule)
    });
  };

  const calculateFromPayment = () => {
    const payment = parseFloat(monthlyPayment) || 0;
    const term = parseInt(loanTerm) || 60;
    const rate = (parseFloat(interestRate) || 0) / 100;
    const down = parseFloat(downPayment) || 0;
    const tradeIn = parseFloat(tradeInValue) || 0;
    const owedOnTrade = parseFloat(amountOwedOnTrade) || 0;
    const taxRate = (parseFloat(salesTax) || 0) / 100;
    const fees = parseFloat(titleFees) || 0;

    const monthlyRate = rate / 12;
    let loanAmount;
    
    if (monthlyRate === 0) {
      loanAmount = payment * term;
    } else {
      loanAmount = payment * (Math.pow(1 + monthlyRate, term) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, term));
    }

    const tradeInCredit = Math.max(0, tradeIn - owedOnTrade);
    
    // Reverse calculate the auto price
    // loanAmount = price - down - tradeInCredit + (price * taxRate) + fees (if included)
    // Solving for price with tax included in loan
    let autoPrice;
    if (includeTaxesInLoan) {
      autoPrice = (loanAmount - fees + down + tradeInCredit) / (1 + taxRate);
    } else {
      autoPrice = loanAmount + down + tradeInCredit;
    }

    const salesTaxAmount = autoPrice * taxRate;
    const upfrontPayment = includeTaxesInLoan ? down : down + salesTaxAmount + fees;
    const totalLoanPayments = payment * term;
    const totalInterest = totalLoanPayments - loanAmount;
    const totalCost = autoPrice + salesTaxAmount + fees + totalInterest;

    const schedule = generateSchedule(loanAmount, monthlyRate, payment, term);

    setResult({
      monthlyPayment: payment,
      autoPrice,
      loanAmount,
      salesTaxAmount,
      upfrontPayment,
      totalLoanPayments,
      totalInterest,
      totalCost,
      term,
      fees,
      principalPercent: ((loanAmount / totalLoanPayments) * 100).toFixed(0),
      interestPercent: ((totalInterest / totalLoanPayments) * 100).toFixed(0),
      schedule,
      yearlySchedule: generateYearlySchedule(schedule)
    });
  };

  const generateSchedule = (principal, monthlyRate, payment, term) => {
    const schedule = [];
    let balance = principal;
    
    for (let i = 1; i <= term; i++) {
      const interest = balance * monthlyRate;
      const principalPaid = payment - interest;
      balance = Math.max(0, balance - principalPaid);
      
      schedule.push({
        month: i,
        interest,
        principal: principalPaid,
        balance,
        year: Math.ceil(i / 12)
      });
    }
    return schedule;
  };

  const generateYearlySchedule = (schedule) => {
    const years = [];
    const maxYear = Math.ceil(schedule.length / 12);
    
    for (let year = 1; year <= maxYear; year++) {
      const yearPayments = schedule.filter(s => s.year === year);
      if (yearPayments.length > 0) {
        years.push({
          year,
          interest: yearPayments.reduce((sum, p) => sum + p.interest, 0),
          principal: yearPayments.reduce((sum, p) => sum + p.principal, 0),
          balance: yearPayments[yearPayments.length - 1].balance
        });
      }
    }
    return years;
  };

  const handleCalculate = () => {
    if (calcMode === 'price') {
      calculateFromPrice();
    } else {
      calculateFromPayment();
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(num);
  };

  const PieChart = ({ percent1, percent2 }) => (
    <div className="relative w-28 h-28">
      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#06b6d4" strokeWidth="20"
          strokeDasharray={`${percent1 * 2.51} 251`} />
        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f59e0b" strokeWidth="20"
          strokeDasharray={`${percent2 * 2.51} 251`} strokeDashoffset={`-${percent1 * 2.51}`} />
      </svg>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/financial-calculators" className="text-cyan-400 hover:text-cyan-300">Financial Calculators</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Auto Loan Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-6">Auto Loan Calculator</h1>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            {/* Mode Tabs */}
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button 
                onClick={() => setCalcMode('price')} 
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  calcMode === 'price' 
                    ? 'border-cyan-500 text-cyan-400' 
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Total Price
              </button>
              <button 
                onClick={() => setCalcMode('payment')} 
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  calcMode === 'payment' 
                    ? 'border-cyan-500 text-cyan-400' 
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Monthly Payment
              </button>
            </div>

            <div className="space-y-4">
              {/* Auto Price or Monthly Payment */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">
                  {calcMode === 'price' ? 'Auto Price' : 'Monthly Pay'}
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  {calcMode === 'price' ? (
                    <Input type="number" value={autoPrice} onChange={(e) => setAutoPrice(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  ) : (
                    <Input type="number" value={monthlyPayment} onChange={(e) => setMonthlyPayment(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  )}
                </div>
              </div>

              {/* Loan Term */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Loan Term</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={loanTerm} onChange={(e) => setLoanTerm(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">months</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Interest Rate</label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>

              {/* Cash Incentives (only for price mode) */}
              {calcMode === 'price' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 font-medium w-36">Cash Incentives</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={cashIncentives} onChange={(e) => setCashIncentives(e.target.value)}
                      className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                  </div>
                </div>
              )}

              {/* Down Payment */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Down Payment</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={downPayment} onChange={(e) => setDownPayment(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>

              {/* Trade-in Value */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Trade-in Value</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={tradeInValue} onChange={(e) => setTradeInValue(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                </div>
              </div>

              {/* Amount Owed on Trade-in */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Amount Owed on Trade-in</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={amountOwedOnTrade} onChange={(e) => setAmountOwedOnTrade(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                </div>
              </div>

              {/* State Selector */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Your State</label>
                <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}
                  className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm max-w-[200px]">
                  {usStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* Sales Tax */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Sales Tax</label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.01" value={salesTax} onChange={(e) => setSalesTax(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>

              {/* Title, Registration Fees */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Title, Registration & Fees</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={titleFees} onChange={(e) => setTitleFees(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>

              {/* Include Taxes in Loan Checkbox */}
              <div className="flex items-center gap-3 mt-2">
                <input type="checkbox" id="includeTaxes" checked={includeTaxesInLoan}
                  onChange={(e) => setIncludeTaxesInLoan(e.target.checked)}
                  className="w-4 h-4 accent-cyan-500" />
                <label htmlFor="includeTaxes" className="text-gray-300 text-sm cursor-pointer">
                  Include taxes and fees in loan
                </label>
              </div>

              <Button onClick={handleCalculate} className="mt-4 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
                Calculate
              </Button>
            </div>

            {/* Results */}
            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-xl font-bold text-white">
                    Monthly Pay: <span className="text-green-400">{formatCurrency(result.monthlyPayment)}</span>
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    {calcMode === 'payment' && (
                      <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                        <span className="text-gray-400">Auto Price</span>
                        <span className="font-semibold text-white">{formatCurrency(result.autoPrice)}</span>
                      </div>
                    )}
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Loan Amount</span>
                      <span className="font-semibold text-white">{formatCurrency(result.loanAmount)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Sales Tax</span>
                      <span className="font-semibold text-white">{formatCurrency(result.salesTaxAmount)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Upfront Payment</span>
                      <span className="font-semibold text-white">{formatCurrency(result.upfrontPayment)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total of {result.term} Loan Payments</span>
                      <span className="font-semibold text-white">{formatCurrency(result.totalLoanPayments)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Loan Interest</span>
                      <span className="font-semibold text-white">{formatCurrency(result.totalInterest)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                      <span className="text-cyan-400 font-medium">Total Cost (price, interest, tax, fees)</span>
                      <span className="font-bold text-cyan-400">{formatCurrency(result.totalCost)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center gap-4">
                    <PieChart percent1={result.principalPercent} percent2={result.interestPercent} />
                    <div className="text-sm space-y-1">
                      <p className="text-white font-medium text-center mb-2">Loan Breakdown</p>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{result.principalPercent}% Principal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{result.interestPercent}% Interest</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Amortization Schedule */}
          {result && (
            <div className="mt-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Amortization Schedule</h2>
                <button onClick={() => setShowSchedule(!showSchedule)}
                  className="text-cyan-400 hover:text-cyan-300 text-sm">
                  {showSchedule ? 'Hide Schedule' : 'Show Schedule'}
                </button>
              </div>

              {showSchedule && (
                <>
                  <div className="flex gap-4 mb-4">
                    <button onClick={() => setScheduleView('yearly')}
                      className={`px-4 py-2 rounded text-sm ${scheduleView === 'yearly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}>
                      Annual Schedule
                    </button>
                    <button onClick={() => setScheduleView('monthly')}
                      className={`px-4 py-2 rounded text-sm ${scheduleView === 'monthly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}>
                      Monthly Schedule
                    </button>
                  </div>

                  <div className="overflow-x-auto max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#1a1a1a]">
                        <tr className="border-b border-[#2a2a2a]">
                          <th className="text-left py-2 px-2 text-gray-400">{scheduleView === 'yearly' ? 'Year' : 'Month'}</th>
                          <th className="text-right py-2 px-2 text-gray-400">Interest</th>
                          <th className="text-right py-2 px-2 text-gray-400">Principal</th>
                          <th className="text-right py-2 px-2 text-gray-400">Ending Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scheduleView === 'yearly' ? (
                          result.yearlySchedule.map((row) => (
                            <tr key={row.year} className="border-b border-[#2a2a2a]/50 hover:bg-[#2a2a2a]/30">
                              <td className="py-2 px-2 text-white">{row.year}</td>
                              <td className="py-2 px-2 text-right text-gray-300">{formatCurrency(row.interest)}</td>
                              <td className="py-2 px-2 text-right text-gray-300">{formatCurrency(row.principal)}</td>
                              <td className="py-2 px-2 text-right text-white font-medium">{formatCurrency(row.balance)}</td>
                            </tr>
                          ))
                        ) : (
                          result.schedule.map((row) => (
                            <React.Fragment key={row.month}>
                              <tr className="border-b border-[#2a2a2a]/50 hover:bg-[#2a2a2a]/30">
                                <td className="py-1.5 px-2 text-white">{row.month}</td>
                                <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.interest)}</td>
                                <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.principal)}</td>
                                <td className="py-1.5 px-2 text-right text-white font-medium">{formatCurrency(row.balance)}</td>
                              </tr>
                              {row.month % 12 === 0 && row.month < result.term && (
                                <tr className="bg-[#0d0d0d]">
                                  <td colSpan="4" className="py-1 px-2 text-center text-gray-500 text-xs">
                                    End of year {row.year}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Related Links */}
          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/loan-calculator" className="text-cyan-400 hover:text-cyan-300">Loan Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/mortgage-calculator" className="text-cyan-400 hover:text-cyan-300">Mortgage Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/interest-calculator" className="text-cyan-400 hover:text-cyan-300">Interest Calculator</Link>
          </div>

          {/* Information Sections */}
          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Auto Loans</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Most people turn to auto loans during a vehicle purchase. They work as any generic, secured loan from a 
                financial institution does with a typical term of 36, 60, 72, or 84 months in the U.S. Each month, repayment 
                of principal and interest must be made from borrowers to auto loan lenders.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Dealership Financing vs. Direct Lending</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Direct Lending</h3>
                  <p className="text-gray-400 text-sm">
                    A typical loan originating from a bank, credit union, or financial institution. Provides more leverage 
                    for buyers to negotiate and places stress on the dealer to compete with a better rate.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Dealership Financing</h3>
                  <p className="text-gray-400 text-sm">
                    The auto loan is initiated and completed through the dealership. Convenient for anyone who doesn't want 
                    to spend time shopping or cannot get an auto loan through direct lending.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Common Fees</h2>
              <div className="space-y-3">
                {[
                  { title: 'Sales Tax', desc: 'Most states collect sales tax for auto purchases. Can be financed with the price of the car.' },
                  { title: 'Document Fees', desc: 'Fee collected by the dealer for processing documents like title and registration.' },
                  { title: 'Title & Registration', desc: 'Fee collected by states for vehicle title and registration.' },
                  { title: 'Destination Fee', desc: 'Covers the shipment of the vehicle from the plant to the dealer. Usually $900-$1,500.' },
                ].map((fee) => (
                  <div key={fee.title} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-1">{fee.title}</h3>
                    <p className="text-gray-400 text-sm">{fee.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Auto Loan Strategies</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-cyan-400 font-semibold mb-2">Preparation</h3>
                  <p className="text-gray-400 text-sm">
                    Determine what is affordable before heading to a dealership. Getting pre-approved for an auto loan 
                    through direct lending can aid negotiations.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-cyan-400 font-semibold mb-2">Credit</h3>
                  <p className="text-gray-400 text-sm">
                    Borrowers with excellent credit will receive lower interest rates. Take steps towards achieving 
                    better credit scores before taking out a loan.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-cyan-400 font-semibold mb-2">Early Payoff</h3>
                  <p className="text-gray-400 text-sm">
                    Paying off an auto loan earlier can result in interest savings. Check for early payoff penalties 
                    in your loan contract.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-cyan-400 font-semibold mb-2">Consider Used Cars</h3>
                  <p className="text-gray-400 text-sm">
                    Buying a pre-owned car even a few years old can result in significant savings. New cars depreciate 
                    by 10%+ as soon as they're driven off the lot.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Trade-in Value</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                A trade-in is a process of selling your vehicle to the dealership in exchange for credit toward purchasing 
                another vehicle. In most states that collect sales tax, the tax is based on the difference between the new 
                car and trade-in price.
              </p>
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  <strong>Note:</strong> Some states (CA, DC, HI, KY, MD, MI, MT, VA) do not offer sales tax reduction with 
                  trade-ins. This calculator automatically adjusts based on the state selected.
                </p>
              </div>
            </section>
          </div>

          {/* Financial Calculators */}
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Mortgage', path: '/mortgage-calculator' },
                { name: 'Loan', path: '/loan-calculator' },
                { name: 'Auto Loan', path: '/auto-loan-calculator' },
                { name: 'Interest', path: '/interest-calculator' },
                { name: 'Payment', path: '/payment-calculator' },
                { name: 'Compound Interest', path: '/compound-interest-calculator' },
                { name: 'Sales Tax', path: '/sales-tax-calculator' },
              ].map((calc) => (
                <Link key={calc.path} to={calc.path}
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors">
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AutoLoanCalculatorPage;
